import React from 'react';
import { getBossGlyph } from '../../data/bossGlyphs';

/* THE BOSS HIEROGLYPH, IN ONE PLACE.
 *
 * `data/bossGlyphs.jsx` authors 17 marks — one per roster boss, each encoding the
 * fight's DYNAMIC rather than the disease (Glyca is a glucose wave inside a
 * euglycemia band; Hemora is a seesaw between clot breach and bleed breach). They
 * are drawn in a 24x24 box, stroke-only, weight 1.8, round caps, and they take
 * their colour from the caller because a boss's colour is the roster's, not the
 * glyph's.
 *
 * 🔴 WHY THIS FILE EXISTS. The <g> below was written out by hand at TWO call
 * sites, and the second one says so in its own comment: Landing.jsx's showcase
 * "mirrors the game's call site (ProgressMap.jsx:1259-1271) exactly". Two copies
 * of a six-attribute stroke contract is one copy away from drifting, and the
 * compendium needed a third. A shared component is the alternative to a third
 * hand copy, and the compendium's whole law is that it MOUNTS the game's
 * component rather than re-implementing it.
 *
 * Extracted 2026-08-09 as a pure no-op: the JSX below is character-identical to
 * what both call sites already emitted, and the landing showcase's rendered
 * outerHTML was captured before and after and compared byte for byte.
 *
 * The transform is the caller's, not this file's, because it is a fact about the
 * caller's coordinate system: ProgressMap centres the mark on a map node of
 * radius r, the landing does the same with r=20, and the compendium hands it a
 * plain 24-unit viewBox and needs no transform at all. `bossGlyphTransform(r)`
 * is here so the r-relative form is written once for the two that do.
 */

/* The r-relative centring the two map call sites use. Kept as an expression
   rather than a precomputed constant so its output is byte-identical to the
   inline form it replaced. */
export const bossGlyphTransform = (r) =>
  `translate(${-r * 0.4745}, ${-r * 0.4745}) scale(${(r * 0.949) / 24})`;

/* The stroke contract, for a caller that is already inside an <svg>.
   Every attribute here was on both inline copies; none is new. */
export function BossGlyphGroup({ glyph, transform, stroke, style }) {
  if (!glyph) return null;
  return (
    <g
      transform={transform}
      fill="none"
      stroke={stroke}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      {glyph}
    </g>
  );
}

/* Standalone, for a caller that is NOT inside an <svg> — the compendium's
   undrawn-assets page. Resolves the mark itself through `getBossGlyph`, which is
   the SAME resolver ProgressMap calls (bossGlyphs.jsx:237), so the two aliases
   it knows about (lurker -> athero, phantom -> septyx) resolve identically here
   and a renamed class cannot show one mark on the map and another here.
   Returns null for a boss with no mark, which is the registry's honest state:
   Ten Shin and his five axis wardens have none, by design — he is System B
   raster art and was never in BOSS_GLYPHS. */
export default function BossGlyph({ node, boss, size = 24, color = 'currentColor', label }) {
  /* `node` takes precedence and is the faithful form: the resolver reads FOUR
     places — boss_class, meta.enemy.class, meta.enemy.icon, and a type-level
     fallback to Septyx for a final_boss node — and the final boss's roster row
     carries no class at all, so the `boss` shorthand alone cannot resolve him.
     The shorthand stays for callers that genuinely have only a class string. */
  const glyph = getBossGlyph(node || { boss_class: boss });
  if (!glyph) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label={label || `${boss} glyph`}
      style={{ color, display: 'block' }}
    >
      <BossGlyphGroup glyph={glyph} stroke={color} />
    </svg>
  );
}
